/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { CoachSettingsContext } from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsContextProvider';
import SettingsSwitch from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsSwitch';
import { useSettingsNotification } from 'bundles/ai-coach-platform/components/patterns/chat/settings/useSettingsNotification';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import { buildLearnerHelpUrl } from 'bundles/common/utils/urlUtils';

import _t from 'i18n!nls/ai-coach-platform';

import SettingsPage from '../SettingsPage';

const styles = {
  root: css`
    height: 100%;
  `,

  memoryItemsContainer: css`
    width: 100%;
    height: auto;
    margin-top: var(--cds-spacing-300);
  `,

  emptyState: css`
    height: 300px;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-200);
  `,

  notification: css`
    position: absolute;
    bottom: 96px;
    max-width: 500px;
    z-index: 99999;
    margin: 0 auto;
    left: 0;
    right: 0;
  `,
  footer: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
};

type Props = {
  onBack: () => void;
};

export const SettingsMemory = (props: Props): React.ReactElement => {
  const { onBack } = props;
  const track = useTracker();
  const trackingData = useCoachTrackingContext()?.data;
  const { updateData, data } = useContext(CoachSettingsContext);
  const { addNotification, clearNotification } = useSettingsNotification();

  const [toggle, setToggle] = useState<boolean>(!!data.memory?.enabled);

  React.useEffect(() => {
    track('view_coach_settings', {
      pageType: 'memory',
      coach: {
        mode: trackingData?.mode,
      },
    });
  }, []); // eslint-disable-line

  React.useEffect(() => {
    return () => {
      clearNotification();
    };
  }, [clearNotification]);

  const handleToggle = (newSelected: boolean) => {
    // optimistically update toggle, revert on error
    setToggle(newSelected);

    // TODO: save changes to BE
    updateData({ ...data, memory: { enabled: newSelected } });

    addNotification({
      content: newSelected
        ? _t(`You've opted-in to Memory. Coach will now save relevant information from your chats.`)
        : _t(`You've opted-out of Memory. Coach will no longer save relevant information from your chats.`),
      severity: 'success',
      isTransient: true,
    });

    track('interact_coach_settings', {
      settingsInteractionType: `toggle_memory_${newSelected ? 'on' : 'off'}`,
    });
  };

  const deleteCount = 60;
  const deleteAllCount = 1000;

  return (
    <div className="coach-settings-memory" css={styles.root}>
      <SettingsPage
        onBack={onBack}
        title={_t('Memory')}
        footer={
          <div css={styles.footer}>
            <CoachButton disabled={true} aria-disabled={true} onClick={() => {}}>
              {_t('Delete (#{count})', { count: deleteCount })}
            </CoachButton>
            <CoachButton disabled={true} aria-disabled={true} onClick={() => {}} variant="ghost">
              {_t('Delete all (#{count})', { count: deleteAllCount })}
            </CoachButton>
          </div>
        }
      >
        <SettingsSwitch
          disabled={false}
          label={_t('Memory')}
          supportText={
            <Typography2 variant="bodySecondary" component="div" color="supportText">
              <Typography2 variant="bodySecondary" component="p" color="supportText">
                {_t(
                  `Coach will remember your details and preferences from past chats to improve your learning experience.
                    This may include information you've shared about your interests or goals, or how you prefer Coach to 
                    respond (e.g. use bullets).`
                )}
                &nbsp;
                <Link
                  href={buildLearnerHelpUrl('000002224-Advance-your-learning-with-Coursera-Coach')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {_t('Learn more about memory')}
                </Link>
              </Typography2>
            </Typography2>
          }
          selected={toggle}
          onToggle={handleToggle}
        />

        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {_t('You can delete any memories Coach has collected below that you feel are not relevant or accurate.')}
        </Typography2>

        <div className="memory-items-container" css={styles.memoryItemsContainer}>
          <div css={styles.emptyState}>
            <Typography2 variant="bodySecondary" color="supportText" component="div">
              {_t('Your details and preferences from past chats will be displayed here.')}
            </Typography2>
          </div>
        </div>
      </SettingsPage>
    </div>
  );
};

export default SettingsMemory;
